import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Badge,
  Icon,
  InlineStack,
  List,
  ListItem,
  Paragraph,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { FAQ, INCLUDED_CATEGORIES, PRICING_GROUPS, SPEND_CONTROLS } from '../content';
import { PlanTable } from './plan-table';
import { SheetCallout } from './sheet-callout';
import { SheetSection } from './sheet-section';

export function FlexPlanSheetBody() {
  return (
    <Stack gap="500">
      <SheetSection
        eyebrow="How we charge"
        heading="What you can send"
        lede="Messaging people inside your own product is free, with no time limit. Reaching them anywhere else needs a card and a funded balance, and you pay only for what you send."
      >
        <PlanTable caption="Channels and pricing" groups={PRICING_GROUPS} />
      </SheetSection>

      {/* "Check eligibility" has no destination yet. */}
      <SheetCallout
        summary="Raised under $10M? Get your first year on us."
        description="Early-stage companies get 12 months of funded usage through our Startup Program, so you can send email from day one without adding funds."
        actionLabel="Check eligibility"
      />

      <SheetSection
        eyebrow="What's included"
        heading="What you get on Flex"
        lede={
          <>
            Grouped the same way as the rest of the platform. Rows marked{' '}
            <Badge color="accent" variant="solid">
              Add-on
            </Badge>{' '}
            are bought inside the product once your account is funded.
          </>
        }
      >
        <Stack gap="100">
          {INCLUDED_CATEGORIES.map((category, index) => (
            <Accordion key={category.title} variant="bordered" defaultOpen={index === 0}>
              <AccordionTitle>
                <InlineStack gap="100" alignBlock="baseline">
                  <Text size="sm" weight="semibold">
                    {category.title}
                  </Text>
                  <Text size="xs" color="secondary">
                    {category.summary}
                  </Text>
                </InlineStack>
              </AccordionTitle>
              <AccordionContent>
                <PlanTable caption={`${category.title} on Flex`} groups={[{ rows: category.rows }]} />
              </AccordionContent>
            </Accordion>
          ))}
        </Stack>
      </SheetSection>

      <SheetSection
        eyebrow="You decide what you spend"
        lede="Usage pricing shouldn't mean an invoice you didn't expect. You put money in, and nothing takes more than you've put there."
      >
        <List styleType="none" gap="100">
          {SPEND_CONTROLS.map((item) => (
            <ListItem key={item}>
              <InlineStack gap="100" alignBlock="start">
                <Icon name="check" color="success" size="sm" flexShrink="0" />
                <Text size="sm">{item}</Text>
              </InlineStack>
            </ListItem>
          ))}
        </List>
      </SheetSection>

      <SheetSection eyebrow="Frequently asked questions">
        <Stack gap="100">
          {FAQ.map((entry) => (
            <Accordion key={entry.question} variant="bordered">
              <AccordionTitle>
                <Text size="sm" weight="semibold">
                  {entry.question}
                </Text>
              </AccordionTitle>
              <AccordionContent>
                <Paragraph size="sm" color="secondary">
                  {entry.answer}
                </Paragraph>
              </AccordionContent>
            </Accordion>
          ))}
        </Stack>
      </SheetSection>

      <Stack gap="150">
        {/* "Book a demo" has no destination yet. */}
        <SheetCallout
          summary="Need more than Flex?"
          description="Premium starts at $1,000 a month, billed yearly, with custom volumes, custom segment limits, and dedicated support. Enterprise adds dedicated infrastructure and governance."
          actionLabel="Book a demo"
        />
        <Text size="xxs" color="secondary">
          Prices, quantities, and which capabilities are add-ons are illustrative and pending the financial model.
        </Text>
      </Stack>
    </Stack>
  );
}
